import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Package, PackageX, Search, Archive, Calendar, Trash2, Users, FileText } from "lucide-react"

import { cn } from "@/shared/lib/utils/UtilsExports"
import { Button } from "@/shared/components/ui/button"

const emptyVariants = cva(
  "flex flex-col items-center justify-center text-center",
  {
    variants: {
      variant: {
        default: "py-12",
        compact: "py-8",
        large: "py-16",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Icon mapping for different empty states
const iconMap = {
  packages: Package,
  "no-packages": PackageX,
  search: Search,
  archived: Archive,
  expired: Calendar,
  deleted: Trash2,
  users: Users,
  documents: FileText,
  default: Package,
} as const

export interface EmptyProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof emptyVariants> {
  icon?: keyof typeof iconMap | React.ComponentType<{ className?: string }>
  title?: string
  description?: string
  action?: {
    label: string
    onClick: () => void
    variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive"
    className?: string
  }
}

const Empty = React.forwardRef<HTMLDivElement, EmptyProps>(
  ({ className, variant, icon = "default", title, description, action, ...props }, ref) => {
    // Determine the icon to render
    const IconComponent = React.useMemo(() => {
      if (typeof icon === "string") {
        return iconMap[icon] || iconMap.default
      }
      return icon
    }, [icon])

    return (
      <div
        ref={ref}
        className={cn(emptyVariants({ variant }), className)}
        {...props}
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <IconComponent className="h-8 w-8 text-muted-foreground" />
        </div>
        
        {title && (
          <h3 className="mt-4 text-lg font-semibold text-foreground">
            {title}
          </h3>
        )}
        
        {description && (
          <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
            {description}
          </p>
        )}
        
        {action && (
          <Button
            onClick={action.onClick}
            variant={action.variant || "default"}
            className={cn("mt-6", action.className)}
          >
            {action.label}
          </Button>
        )}
      </div>
    )
  }
)

Empty.displayName = "Empty"

export { Empty, emptyVariants }